import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Key } from 'w3c-keys';
import { Popover } from '../popover';
import { SwatchShapeOptions } from '../selectors';
import { BorderProps, Color } from '../selectors/color/color';
import { Font } from '../selectors/font-selector/font';
import { ColorPickerMenu, ColorPickerProps } from './colorpicker-menu';

export enum SELECTOR_TYPE {
  FONT = 'FONT',
  COLOR = 'COLOR',
}

type ColorPickerPropDef = ColorPickerProps & {
  headerSelectorType?: SELECTOR_TYPE;
  headerTitle?: string;

  /**
   * Color picker Swatch Shape. Applicable only for Color Selector.
   * `circle` for using legends
   * @default 'default'
   */
  swatchShape?: SwatchShapeOptions;
  isReferenceVisible?: boolean;
  /** if gradient-picker is enabled or not
   * @default 'false'
   */
  isGradientEnabled?: boolean;
  /** a11y prop */
  tabIndex?: number;
  /**
   * Customize the border style to override the default color selector border.
   * This is used in the chart legend when series points are hovered over or selected,
   * changing the legend color selector border to the primary blue color.
   *
   * @default undefined
   */
  border?: BorderProps;
};

export const ColorPicker: FC<ColorPickerPropDef> = ({
  headerSelectorType = SELECTOR_TYPE.COLOR,
  headerTitle,
  defaultValue = '',
  viewType,
  onChange,
  swatchShape = SwatchShapeOptions.DEFAULT,
  isReferenceVisible = false,
  isGradientEnabled = false,
  tabIndex = 0,
  border = undefined,
  className, 
  ...restProps
}: ColorPickerPropDef) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [target, setTarget] = useState<any>();
  const [currentSelectedOption, setCurrentSelectedOption] = useState<any>(defaultValue);
  const menuRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const wrapperRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [popoverAlign, setPopoverAlign] = useState<'bottom-start' | 'top-start'>('bottom-start');

  useEffect(() => {
    setCurrentSelectedOption(defaultValue);
  }, [defaultValue]);

  const onChangeHandler = (selectedColor: string) => {
    // default color string's case gets toggled within the color picker
    if (currentSelectedOption.toUpperCase() !== selectedColor.toUpperCase()) {
      setCurrentSelectedOption(selectedColor);
      onChange!(selectedColor);
    }
  };

  const onHeaderChangeHandler = (event: any, isVisible: any) => {
    setTarget(event);
    setVisible(isVisible);
  };

  const handleDocumentEvent = useCallback(
    (event: any) => {
      if (
        (menuRef.current && menuRef.current.contains(event.target)) ||
        (target && target.current && target.current.contains(event.target))
      ) {
        // click handler inside select menu options
        return;
      }
      setVisible(false);
    },
    [target]
  );

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== Key.Escape || !visible) return;
      wrapperRef.current?.focus();
      setVisible(false);
    },
    [visible]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentEvent, {
      capture: false,
    });
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleDocumentEvent, {
        capture: false,
      });
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleDocumentEvent, handleEscape, menuRef]);

  useEffect(() => {
    if (visible) {
      menuRef.current?.focus();
    }
  }, [visible]);

  const checkPopoverPosition = useCallback(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const isOutOfViewport = rect.bottom >= window.innerHeight;
      setPopoverAlign(isOutOfViewport ? 'top-start' : 'bottom-start');
    }
  }, [menuRef]);

  useLayoutEffect(() => {
    if (visible) {
      checkPopoverPosition();
    }
  }, [visible, checkPopoverPosition]);

  return (
    <div className={className}>
      {headerSelectorType === SELECTOR_TYPE.COLOR ? (
        <Color
          title={headerTitle}
          selectedOption={currentSelectedOption}
          onChange={onHeaderChangeHandler}
          isToggle={visible}
          swatchShape={swatchShape}
          showBorder
          border={border}
          isGradientEnabled={isGradientEnabled}
          baseDivRef={wrapperRef}
          tabIndex={tabIndex}
          ariaLabel={'Color'}
        />
      ) : (
        <Font
          title={headerTitle}
          selectedOption={currentSelectedOption}
          onChange={onHeaderChangeHandler}
          isToggle={visible}
          baseDivRef={wrapperRef}
          ariaLabel={'Font'}
        />
      )}
      <Popover
        content={
          <ColorPickerMenu
            viewType={viewType}
            defaultValue={currentSelectedOption}
            onChange={onChangeHandler}
            isVisible={visible}
            setIsVisible={(isVisible: boolean) => setVisible(isVisible)}
            targetRef={target}
            menuRef={menuRef}
            {...restProps}
          />
        }
        targetRef={target}
        popperRef={menuRef}
        align={popoverAlign}
        appendToBody
        isVisible={visible}
        isReferenceVisible={isReferenceVisible}
        autoFocusOnVisible
      />
    </div>
  );
};
